import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  pageSectionHeaderSx,
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../constants/pageSectionStyles";
import {
  regulationDetailsSectionTabStripSx,
  regulationDetailsSectionTabsSx,
} from "../constants/regulationDetailsNavTabStyles";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../hooks/useGuardedAsyncCallback";
import { useAcTenantId } from "../hooks/useAcTenantId";
import { useHasPrivilege, useHasPrivilegeStatus } from "../hooks/useHasPrivilege";
import {
  fetchAutoTaggingEnabled,
  updateAutoTaggingEnabled,
} from "../services/autoTaggingService";
import {
  AI_PLAN_PAGE_READ_PRIVILEGES,
  AUTO_TAGGING_PRIVILEGES,
  CONDITIONAL_TAGGING_PRIVILEGES,
  OBJECT_TO_OBJECT_PRIVILEGES,
} from "../services/permissionsService";
import TaggingPlanTab from "./AiPlanPage/TaggingPlan/TaggingPlanTab";
import EnhancedTaggingRuleTab from "./AiPlanPage/EnhancedTaggingRule/EnhancedTaggingRuleTab";
import ObjectToObjectPlanTab from "./AiPlanPage/ObjectToObjectPlan/ObjectToObjectPlanTab";
import TriagePlanTab from "./AiPlanPage/TriagePlan/TriagePlanTab";
import AutoTaggingConfirmDialog, {
  type AutoTaggingConfirmMode,
} from "./AiPlanPage/AutoTaggingConfirmDialog";
import {
  AI_PLAN_TAB_LABELS,
  AI_PLAN_TAB_QUERY_KEY,
  normalizeAiPlanTabKey,
  type AiPlanTabKey,
} from "./AiPlanPage/constants";
import { useRegulationsNextShellIntegration } from "./shared/useRegulationsNextShellIntegration";

/** Temporarily hide the page-level Auto-tagging master switch (and its APIs). */
const SHOW_AUTO_TAGGING_MASTER_SWITCH = false;

/** Temporarily hide Triage Plan until the feature is released. */
const SHOW_TRIAGE_PLAN_TAB = false;

export default function AiPlanPage() {
  useRegulationsNextShellIntegration();
  const { showMessage } = useSnackbar();
  const acTenantId = useAcTenantId();
  const { has: canOpenAiPlanPage, loaded: privilegesLoaded } =
    useHasPrivilegeStatus([...AI_PLAN_PAGE_READ_PRIVILEGES], "any");
  const canReadAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.read);
  const canReadConditionalTagging = useHasPrivilege(
    CONDITIONAL_TAGGING_PRIVILEGES.read,
  );
  const canReadObjectToObject = useHasPrivilege(
    OBJECT_TO_OBJECT_PRIVILEGES.read,
  );
  // No dedicated execute privilege in aihub authz — manage controls the master switch.
  const canExecuteAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(AI_PLAN_TAB_QUERY_KEY);

  const visibleTabs = useMemo(() => {
    const tabs: AiPlanTabKey[] = [];
    if (canReadAutoTagging) {
      tabs.push("taggingPlan");
    }
    if (canReadConditionalTagging) {
      tabs.push("enhancedAssessmentPlan");
    }
    if (canReadObjectToObject) {
      tabs.push("objectToObjectPlan");
    }
    if (SHOW_TRIAGE_PLAN_TAB && canReadAutoTagging) {
      tabs.push("triagePlan");
    }
    return tabs;
  }, [canReadAutoTagging, canReadConditionalTagging, canReadObjectToObject]);

  // URL is the source of truth — avoids a race where setTab + setSearchParams
  // briefly leaves tabFromUrl on the previous tab and the sync effect reverts.
  const tabFromUrlNormalized = normalizeAiPlanTabKey(tabFromUrl);
  const tab: AiPlanTabKey =
    (tabFromUrlNormalized && visibleTabs.includes(tabFromUrlNormalized)
      ? tabFromUrlNormalized
      : null) ??
    visibleTabs[0] ??
    "taggingPlan";
  const [autoTaggingEnabled, setAutoTaggingEnabled] = useState(false);
  const [autoTaggingStatusLoading, setAutoTaggingStatusLoading] = useState(true);
  const [autoTaggingConfirmMode, setAutoTaggingConfirmMode] =
    useState<AutoTaggingConfirmMode | null>(null);
  const [savingAutoTagging, setSavingAutoTagging] = useState(false);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: AiPlanTabKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "taggingPlan") {
            next.delete(AI_PLAN_TAB_QUERY_KEY);
          } else {
            next.set(AI_PLAN_TAB_QUERY_KEY, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (visibleTabs.includes(tab)) return;
    handleTabChange({} as React.SyntheticEvent, visibleTabs[0]);
  }, [handleTabChange, tab, visibleTabs]);

  useEffect(() => {
    if (!SHOW_AUTO_TAGGING_MASTER_SWITCH || !canReadAutoTagging) {
      setAutoTaggingStatusLoading(false);
      return;
    }

    let cancelled = false;

    setAutoTaggingStatusLoading(true);
    fetchAutoTaggingEnabled()
      .then((enabled) => {
        if (!cancelled) {
          setAutoTaggingEnabled(enabled);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAutoTaggingStatusLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [acTenantId, canReadAutoTagging]);

  const handleAutoTaggingSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canExecuteAutoTagging) return;
      setAutoTaggingConfirmMode(event.target.checked ? "turnOn" : "turnOff");
    },
    [canExecuteAutoTagging],
  );

  const handleCloseAutoTaggingConfirm = useCallback(() => {
    if (savingAutoTagging) return;
    setAutoTaggingConfirmMode(null);
  }, [savingAutoTagging]);

  const handleConfirmAutoTagging = useGuardedAsyncCallback(async () => {
    if (!autoTaggingConfirmMode || savingAutoTagging || !canExecuteAutoTagging) {
      return;
    }

    const nextEnabled = autoTaggingConfirmMode === "turnOn";
    setSavingAutoTagging(true);
    try {
      const enabled = await updateAutoTaggingEnabled(nextEnabled);
      setAutoTaggingEnabled(enabled);
      setAutoTaggingConfirmMode(null);
      showMessage(
        enabled ? "Auto-tagging turned on." : "Auto-tagging turned off.",
        "success",
      );
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update auto-tagging.",
        "error",
      );
    } finally {
      setSavingAutoTagging(false);
    }
  });

  const showAccessDenied = privilegesLoaded && !canOpenAiPlanPage;
  const showAutoTaggingSwitch =
    SHOW_AUTO_TAGGING_MASTER_SWITCH &&
    canReadAutoTagging &&
    canExecuteAutoTagging;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Box sx={{ ...pageSectionHeaderSx, py: 0, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: "4px",
              }}
            >
              <Typography sx={pageSectionTitleSx}>
                Classification AI Plan
              </Typography>
              {showAutoTaggingSwitch ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={autoTaggingEnabled}
                      onChange={handleAutoTaggingSwitchChange}
                      disabled={autoTaggingStatusLoading || savingAutoTagging}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
                      Auto-tagging
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              ) : null}
            </Box>
            <Typography sx={pageSectionSubtitleSx}>
              Define assessment logic, values, weights, and triage rules to
              ensure consistent AI evaluation and accurate downstream process.
            </Typography>
          </Box>
        </Box>

        {!showAccessDenied ? (
          <Box sx={regulationDetailsSectionTabStripSx}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={regulationDetailsSectionTabsSx}
            >
              {visibleTabs.map((tabKey) => (
                <Tab
                  key={tabKey}
                  value={tabKey}
                  label={AI_PLAN_TAB_LABELS[tabKey].line1}
                />
              ))}
            </Tabs>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "#f9fafb",
          pl: 0,
          py: "16px",
        }}
      >
        {showAccessDenied ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You do not have permission to view Classification AI Plan.
            </Typography>
          </Box>
        ) : (
          <>
            {tab === "taggingPlan" ? (
              <TaggingPlanTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
            {tab === "enhancedAssessmentPlan" ? (
              <EnhancedTaggingRuleTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
            {tab === "objectToObjectPlan" ? (
              <ObjectToObjectPlanTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
            {tab === "triagePlan" ? (
              <TriagePlanTab key={acTenantId || "no-ac-tenant"} />
            ) : null}
          </>
        )}
      </Box>

      {SHOW_AUTO_TAGGING_MASTER_SWITCH ? (
        <AutoTaggingConfirmDialog
          open={autoTaggingConfirmMode !== null}
          mode={autoTaggingConfirmMode}
          saving={savingAutoTagging}
          onClose={handleCloseAutoTaggingConfirm}
          onConfirm={handleConfirmAutoTagging}
        />
      ) : null}
    </Box>
  );
}
